import { useEffect, useMemo } from "react"
import {
  createEngineRunner,
  EngineScriptError,
} from "features/Dashboard/runtime/engineRunner"
import {
  QueryQueue,
  SupersededRunError,
} from "features/Dashboard/runtime/queryQueue"
import { useTabSpecStore, useTabSpecStoreApi } from "./tabSpecStore"
import { useEngineCommands } from "./useEngineCommands"

/**
 * One queue per session instance and generation; a new session starts a
 * fresh one. Its runner refuses to send a script once the session has moved
 * on (a new generation, or one that is not ready), so a superseded queue
 * cannot run into the next session.
 */
export const useTabSpecQueue = (): QueryQueue | null => {
  const store = useTabSpecStoreApi()
  const instance = useTabSpecStore((s) => s.session.instance)
  const generation = useTabSpecStore((s) => s.session.generation)
  // The table requests may take 5 minutes each (`engineTimeout.ts`); the session's have no limit.
  const { RunEngineQuery, pollingAsync, getAsyncResult } = useEngineCommands({ timeout: true })
  const queue = useMemo(() => {
    if (!instance) return null
    const isLive = (): boolean => {
      const { session } = store.getState()
      return session.generation === generation && session.status === "ready"
    }
    const run = createEngineRunner({
      run: async (script) => {
        if (!isLive()) throw new SupersededRunError()
        // A request without a query takes the Conductor down: never send one.
        if (!script.trim()) throw new EngineScriptError("There is no script to run.", [])
        const r = await RunEngineQuery(instance, script, true)
        return { status: r.status, data: r.data }
      },
      poll: async (id) => ({ status: (await pollingAsync(id)).status }),
      fetch: async (id) => ({ data: (await getAsyncResult(id)).data }),
    })
    // A read of the lineage waits its turn here too, and runs only while the session is ready:
    // its preparation runs outside the queue.
    return new QueryQueue(run, isLive)
    // `generation` is in the key on purpose: a new session invalidates cached
    // results, so it gets a fresh queue. The engine commands keep their
    // identity for the life of the screen (`useEngineCommands`), so they
    // never replace the queue of a running session.
  }, [store, instance, generation, RunEngineQuery, pollingAsync, getAsyncResult])

  // A replaced queue, and the last one on unmount, stop sending. The queue is
  // built in render, so its users get the new one in the same commit. It is
  // always null at mount (a fresh store has no instance), so StrictMode's
  // mount-time cleanup never disposes a live queue.
  useEffect(() => () => queue?.dispose(), [queue])

  return queue
}
